/**
 * Format AI response text to HTML with proper styling
 * Handles markdown-like formatting, links, emails, tables, code blocks, etc.
 * @param {string} text - Raw AI response text
 * @returns {string} - Formatted HTML string
 */
export const formatAIResponse = (text) => {
    if (!text || typeof text !== "string") {
        return "";
    }

    let formatted = text;

    // Step 1: Escape HTML to prevent XSS, but preserve line breaks
    formatted = formatted.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");

    // Step 2: Detect and format code blocks (```code```)
    formatted = formatted.replace(/```([a-z]*)\n([\s\S]*?)```/gi, (match, lang, code) => {
        return `<pre class="code-block"><code class="language-${lang || "text"}">${code.trim()}</code></pre>`;
    });

    // Step 3: Detect and format inline code (`code`)
    formatted = formatted.replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>');

    // Step 4: Format headings (### Heading, ## Heading, # Heading)
    formatted = formatted.replace(/^### (.+)$/gm, "<h3>$1</h3>");
    formatted = formatted.replace(/^## (.+)$/gm, "<h2>$1</h2>");
    formatted = formatted.replace(/^# (.+)$/gm, "<h1>$1</h1>");

    // Step 5: Format bold text (**text** or __text__)
    // Updated regex to handle multiline and text with colons like **Assessment:**
    formatted = formatted.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    formatted = formatted.replace(/__([^_]+)__/g, "<strong>$1</strong>");

    // Step 6: Format italic text (*text* or _text_)
    // Use word boundary to avoid matching list markers
    formatted = formatted.replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, "<em>$1</em>");
    formatted = formatted.replace(/(?<!_)_([^_\n]+)_(?!_)/g, "<em>$1</em>");

    // Step 7: Format strikethrough (~~text~~)
    formatted = formatted.replace(/~~(.+?)~~/g, "<del>$1</del>");

    // Step 8: Format links [text](url)
    formatted = formatted.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

    // Step 9: Auto-link URLs (http://, https://)
    formatted = formatted.replace(/(?<!href=")(?<!src=")(https?:\/\/[^\s<>"]+)/gi, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');

    // Step 10: Auto-link email addresses
    formatted = formatted.replace(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/gi, '<a href="mailto:$1">$1</a>');

    // Step 11: Format unordered lists (- item or * item)
    // Match bullet lists with flexible spacing and wrap in <ul>
    formatted = formatted.replace(/(?:^|\n)((?:[-*]\s+.+(?:\n|$))+)/gm, (match) => {
        const items = match
            .trim()
            .split("\n")
            .map((line) => {
                // Handle flexible spacing after bullet (e.g., "-   " or "- ")
                const content = line.replace(/^[-*]\s+/, "").trim();
                return content ? `<li>${content}</li>` : "";
            })
            .filter(Boolean)
            .join("");
        return items ? `<ul>${items}</ul>` : "";
    });

    // Step 12: Format ordered lists (1. item, 2. item, etc.)
    // Match numbered lists and wrap in <ol>
    formatted = formatted.replace(/(?:^|\n)((?:\d+\.\s+.+(?:\n|$))+)/gm, (match) => {
        const items = match
            .trim()
            .split("\n")
            .map((line) => {
                const content = line.replace(/^\d+\.\s+/, "").trim();
                return content ? `<li>${content}</li>` : "";
            })
            .filter(Boolean)
            .join("");
        return items ? `<ol>${items}</ol>` : "";
    });

    // Step 13: Format blockquotes (> text)
    formatted = formatted.replace(/^&gt; (.+)$/gm, "<blockquote>$1</blockquote>");

    // Step 14: Format horizontal rules (---, ***, ___)
    formatted = formatted.replace(/^(?:---|\*\*\*|___)$/gm, "<hr>");

    // Step 15: Convert line breaks to <br> tags (preserve paragraph structure)
    formatted = formatted.replace(/\n\n/g, "</p><p>");
    formatted = formatted.replace(/\n/g, "<br>");

    // Step 16: Wrap in paragraph tags
    formatted = `<p>${formatted}</p>`;

    // Step 17: Clean up empty paragraphs and list artifacts
    formatted = formatted.replace(/<p><\/p>/g, "");
    formatted = formatted.replace(/<p>\s*<\/p>/g, "");
    formatted = formatted.replace(/<br>\s*<ul>/g, "<ul>");
    formatted = formatted.replace(/<\/ul>\s*<br>/g, "</ul>");
    formatted = formatted.replace(/<br>\s*<ol>/g, "<ol>");
    formatted = formatted.replace(/<\/ol>\s*<br>/g, "</ol>");

    // Step 18: Fix nested lists (remove extra <ul>/<ol> tags)
    formatted = formatted.replace(/<\/ul>\s*<ul>/g, "");
    formatted = formatted.replace(/<\/ol>\s*<ol>/g, "");

    // Step 19: Format tables (detect simple table patterns)
    formatted = formatTables(formatted);

    return formatted;
};

/**
 * Detect and format tables in the text
 * Looks for patterns like:
 * | Header 1 | Header 2 |
 * |----------|----------|
 * | Cell 1   | Cell 2   |
 */
const formatTables = (text) => {
    const lines = text.split("<br>");
    let inTable = false;
    let tableRows = [];
    let result = [];

    lines.forEach((line, index) => {
        const trimmedLine = line.trim();

        // Check if line looks like a table row
        if (trimmedLine.startsWith("|") && trimmedLine.endsWith("|")) {
            const cells = trimmedLine
                .slice(1, -1)
                .split("|")
                .map((cell) => cell.trim());

            // Check if it's a separator row (|---|---|)
            const isSeparator = cells.every((cell) => /^[-:]+$/.test(cell));

            if (isSeparator) {
                // Skip separator rows
                return;
            }

            if (!inTable) {
                inTable = true;
                tableRows = [];
            }

            tableRows.push(cells);
        } else {
            // End of table, flush accumulated rows
            if (inTable && tableRows.length > 0) {
                result.push(buildTableHTML(tableRows));
                tableRows = [];
                inTable = false;
            }
            result.push(line);
        }
    });

    // Flush any remaining table rows
    if (inTable && tableRows.length > 0) {
        result.push(buildTableHTML(tableRows));
    }

    return result.join("<br>");
};

/**
 * Build HTML table from array of rows
 * @param {Array} rows - Array of row arrays
 * @returns {string} - HTML table
 */
const buildTableHTML = (rows) => {
    if (!rows || rows.length === 0) return "";

    let html = '<table class="askai-table">';

    // First row is header
    html += "<thead><tr>";
    rows[0].forEach((cell) => {
        html += `<th>${cell}</th>`;
    });
    html += "</tr></thead>";

    // Remaining rows are body
    if (rows.length > 1) {
        html += "<tbody>";
        rows.slice(1).forEach((row) => {
            html += "<tr>";
            row.forEach((cell) => {
                html += `<td>${cell}</td>`;
            });
            html += "</tr>";
        });
        html += "</tbody>";
    }

    html += "</table>";
    return html;
};

export default formatAIResponse;
